import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    DownloadOutlined,
    FullscreenOutlined,
    MinusOutlined,
    PlusOutlined,
    RotateLeftOutlined,
    RotateRightOutlined,
    UndoOutlined
} from "@ant-design/icons";
import {Tooltip} from "antd";

/**
 * ImageViewer
 * - src: string (url, blob url, or data url)
 * - alt?: string
 * - className?: string
 */
export default function ImageViewer({ src, alt = "image", className = "" }) {
    const containerRef = useRef(null);

    const [scale, setScale] = useState(1);
    const [rotation, setRotation] = useState(0); // degrees
    const [translate, setTranslate] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);

    const dragRef = useRef({
        startX: 0,
        startY: 0,
        startTX: 0,
        startTY: 0,
    });

    const canShow = !!src;

    const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

    const reset = useCallback(() => {
        setScale(1);
        setRotation(0);
        setTranslate({ x: 0, y: 0 });
    }, []);

    const zoomIn = useCallback(() => setScale((s) => clamp(Number((s * 1.2).toFixed(4)), 0.1, 10)), []);
    const zoomOut = useCallback(() => setScale((s) => clamp(Number((s / 1.2).toFixed(4)), 0.1, 10)), []);
    const rotateLeft = useCallback(() => setRotation((r) => (r - 90) % 360), []);
    const rotateRight = useCallback(() => setRotation((r) => (r + 90) % 360), []);

    const download = useCallback(() => {
        if (!src) return;
        const a = document.createElement("a");
        a.href = src;
        a.download = "image";
        a.rel = "noopener";
        a.click();
    }, [src]);

    const fitToScreen = useCallback(() => {
        // Fit based on container size and image natural size (best-effort).
        // We can’t reliably know natural size until load; so we keep it simple:
        setScale(1);
        setTranslate({ x: 0, y: 0 });
    }, []);

    const onWheel = useCallback(
        (e) => {
            if (!containerRef.current) return;
            e.preventDefault();

            const delta = e.deltaY;
            const zoomFactor = delta > 0 ? 1 / 1.15 : 1.15;

            setScale((s) => clamp(Number((s * zoomFactor).toFixed(4)), 0.1, 10));
        },
        []
    );

    const onMouseDown = useCallback((e) => {
        if (e.button !== 0) return;
        setIsDragging(true);
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startTX: translate.x,
            startTY: translate.y,
        };
    }, [translate.x, translate.y]);

    const onMouseMove = useCallback((e) => {
        if (!isDragging) return;
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        setTranslate({ x: dragRef.current.startTX + dx, y: dragRef.current.startTY + dy });
    }, [isDragging]);

    const endDrag = useCallback(() => setIsDragging(false), []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        // Use non-passive to allow preventDefault on wheel.
        const handler = (evt) => onWheel(evt);
        el.addEventListener("wheel", handler, { passive: false });

        return () => el.removeEventListener("wheel", handler);
    }, [onWheel]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (!canShow) return;

            // shortcuts
            if (e.key === "+" || (e.key === "=" && (e.ctrlKey || e.metaKey))) {
                e.preventDefault();
                zoomIn();
            }
            if (e.key === "-" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                zoomOut();
            }
            if (e.key === "0" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                reset();
            }
            if (e.key.toLowerCase() === "r") rotateRight();
            if (e.key === "Escape") reset();
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [canShow, reset, rotateRight, zoomIn, zoomOut]);

    // Reset when src changes
    useEffect(() => {
        reset();
    }, [src, reset]);

    const transform = useMemo(() => {
        // translate -> rotate -> scale feels natural for panning
        return `translate(${translate.x}px, ${translate.y}px) rotate(${rotation}deg) scale(${scale})`;
    }, [translate.x, translate.y, rotation, scale]);

    if (!canShow) {
        return (
            <div className={`w-full h-full flex items-center justify-center border border-zinc-200 ${className}`}>
                <span className="text-zinc-500">No image</span>
            </div>
        );
    }

    return (
        <div className={`w-full h-full flex flex-col ${className}`}>
            {/* Toolbar */}
            <div className="flex items-center gap-2 p-2 border border-zinc-200 bg-white">
                <button className="px-2 py-1 border rounded" onClick={zoomOut} title="Zoom out (Ctrl/⌘ -)">
                    <Tooltip title="Zoom out">
                        <MinusOutlined />
                    </Tooltip>
                </button>
                <button className="px-2 py-1 border rounded" onClick={zoomIn} title="Zoom in (Ctrl/⌘ +)">
                    <Tooltip title="Zoom in">
                        <PlusOutlined />
                    </Tooltip>
                </button>
                <div className="text-sm text-zinc-700 min-w-[80px] text-center">{Math.round(scale * 100)}%</div>

                <button className="px-2 py-1 border rounded" onClick={rotateLeft} title="Rotate left">
                    <Tooltip title="Rotate left">
                        <RotateLeftOutlined />
                    </Tooltip>
                </button>
                <button className="px-2 py-1 border rounded" onClick={rotateRight} title="Rotate right (R)">
                    <Tooltip title="Rotate right">
                        <RotateRightOutlined />
                    </Tooltip>
                </button>

                <button className="px-2 py-1 border rounded" onClick={fitToScreen} title="Fit to screen">
                    <Tooltip title="Fit to screen">
                        <FullscreenOutlined />
                    </Tooltip>
                </button>

                <button className="px-2 py-1 border rounded" onClick={reset} title="Reset (Esc / Ctrl/⌘ 0)">
                    <Tooltip title="Reset">
                        <UndoOutlined />
                    </Tooltip>
                </button>

                <div className="flex-1" />
                <button className="px-2 py-1 border rounded" onClick={download} title="Download">
                    <Tooltip title="Download">
                        <DownloadOutlined />
                    </Tooltip>
                </button>
            </div>

            {/* Canvas */}
            <div
                ref={containerRef}
                className="relative flex-1 overflow-hidden border border-t-0 border-zinc-200 bg-zinc-50 select-none"
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                style={{ cursor: isDragging ? "grabbing" : "grab" }}
            >
                <div className="absolute inset-0 flex items-center justify-center">
                    <img
                        src={src}
                        alt={alt}
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        style={{
                            transform,
                            transformOrigin: "center center",
                            maxWidth: "95%",
                            maxHeight: "95%",
                            userSelect: "none",
                            pointerEvents: "none",
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
